import { reactive } from 'vue'

// Shared state for the multi-step "Add your own offer" wizard. Each routed step
// page reads from and writes to this store so progress is preserved as the user
// moves between the separate wizard pages.

function emptyState() {
  return {
    // Step 1 — retailer
    retailer: {
      id: '',
      retailerName: '',
      retailerWebsite: '',
      retailerLogoFile: undefined,
      retailerLogo: '',
      obCompatiable: undefined,
      monthlyWebVisits: undefined,
      goodForBusiness: undefined,
      luxury: undefined,
      subscription: undefined,
    },
    // true when an existing retailer was selected (rather than created)
    selectedRetailer: false,

    // Step 2 — transaction compatibility
    enrichment: undefined, // { description, enrichmentCompatiable }

    // Step 3 — MIDs / channels
    channels: [], // channel ids
    channelsConfirmed: false,

    // Step 4 + 5 — offer information and cashback rules
    offerInformation: {
      // Supplier is always the client (the publisher); set server-side, never chosen here.
      supplierId: '',
      // Publisher's own internal reference for this offer (their id, not ours).
      internalOfferId: '',
      offerTerms: '',
      spendingCap: false,
      spendingCapAmount: null,
      hidden: false,
      customReporting: false,
      reportingPeriod: null,
      customRefundPeriod: false,
      refundPeriodInDays: null,
      // Banner: hosted URL (offerBanner) or an uploaded file (offerBannerFile, uploaded on submit).
      offerBanner: '',
      offerBannerFile: undefined,
      // Offer-page content blocks (DisplayInformation.*). Content-block images are hosted URLs.
      ctaBanner: { image: '', link: '', buttonText: '' },
      sectionTitle: '',
      sectionDescription: '',
      ctaTiles: [],
      videoSectionTitle: '',
      videoSectionDescription: '',
      videoCta: { videoUrl: '', posterImage: '', link: '', buttonText: '' },
      offerLogo: '',
      offerLogoFile: undefined,
      offerDescription: '',
      lengthOfTime: undefined,
      currency: [],
      country: '',
      offerName: '',
      categories: [],
      link: '',
      offerType: '',
      matchAgainst: 'ALL_CHANNELS',
      superMatch: false,
      unenrichedCreditorsStrings: [],
      channels: [],
      cashbackRules: [
        {
          customerType: 'EXISTING_CUSTOMER',
          lapsedInDays: null,
          offerUsageType: 'MULTI_USE',
          conditions: [
            {
              type: 'GREATER_THAN',
              lowerBound: 0,
              upperBound: null,
              value: {
                type: 'PERCENTAGE',
                totalValue: undefined,
                maxAvailableValue: undefined,
                maximum: undefined,
                endUserValue: undefined,
                supplierValue: 0,
                boogiValue: 0,
                clientValue: undefined,
              },
              fixedDistributorCommission: true,
            },
          ],
        },
      ],
    },

    activateOfferRequest: {
      splitOfCashbackPercentage: { endUser: 50, client: 50 },
      activate: true,
    },

    // Set when the wizard was opened to edit an existing offer rather than create one.
    // The submit is the same call either way: POST /bring-your-own-offer/offer upserts on
    // (supplier, supplierOffersId), so re-submitting with the original supplier offer id
    // updates the offer in place.
    editingOfferId: '',
  }
}

export const addOfferWizard = reactive({
  ...emptyState(),

  reset() {
    Object.assign(this, emptyState())
  },

  setSelectedRetailer(data) {
    this.retailer.id = data.id
    this.retailer.retailerName = data.retailerName
    this.retailer.retailerWebsite = data.retailerWebsite
    this.retailer.retailerLogo = data.logo
    this.retailer.monthlyWebVisits = data.monthlyWebVisits
    this.retailer.goodForBusiness = data.goodForBusiness
    this.retailer.luxury = data.luxury
    this.retailer.subscription = data.subscription
    this.retailer.obCompatiable = data.openBankingPublisherCompatible
    this.selectedRetailer = true
  },

  // Clear the chosen/created retailer so the user can pick a different one.
  // Also clears the downstream steps that depend on it.
  deselectRetailer() {
    const fresh = emptyState()
    Object.assign(this.retailer, fresh.retailer)
    this.selectedRetailer = false
    this.enrichment = undefined
    this.channels = fresh.channels
    this.channelsConfirmed = false
  },

  setCreatedRetailer(data) {
    this.retailer.id = ''
    this.retailer.retailerName = data.retailerName
    this.retailer.retailerWebsite = data.retailerWebsite
    this.retailer.retailerLogo = data.displayUrl
    this.retailer.retailerLogoFile = data.retailerLogoFile
    this.retailer.monthlyWebVisits = data.monthlyWebVisits
    this.retailer.goodForBusiness = data.goodForBusiness
    this.retailer.luxury = data.luxury
    this.retailer.subscription = data.subscription
    this.retailer.obCompatiable = data.obCompatiable
    this.selectedRetailer = false
  },

  // Load an existing offer into the wizard so it can be edited. Everything is already
  // populated, so each step is marked complete and the stepper is fully navigable.
  seedFromOffer(offer) {
    Object.assign(this, emptyState())
    this.editingOfferId = offer.id || ''

    const retailer = offer.retailer || {}
    this.retailer.id = retailer.id || ''
    this.retailer.retailerName = retailer.retailerName || ''
    this.retailer.retailerWebsite = retailer.retailerWebsite || ''
    this.retailer.retailerLogo = retailer.logo || ''
    this.retailer.obCompatiable = retailer.openBankingPublisherCompatible
    this.retailer.monthlyWebVisits = retailer.monthlyWebVisits
    this.retailer.goodForBusiness = retailer.goodForBusiness
    this.retailer.luxury = retailer.luxury
    this.retailer.subscription = retailer.subscription
    this.selectedRetailer = true

    this.channels = (offer.channels || []).map((c) => c.id).filter(Boolean)
    this.channelsConfirmed = true

    const display = offer.displayInformation || {}
    Object.assign(this.offerInformation, {
      offerName: offer.offerName || '',
      // The upsert key: keeping the original id is what makes the submit an update.
      supplierId: (offer.supplierOfferIds && offer.supplierOfferIds[0]) || '',
      internalOfferId: offer.internalOfferId || '',
      spendingCap: !!offer.spendingCapped,
      spendingCapAmount: offer.spendingCapAmount ?? null,
      hidden: !!offer.hidden,
      customReporting: !!offer.customReporting,
      reportingPeriod: offer.reportingPeriod ?? null,
      customRefundPeriod: !!offer.customRefundPeriod,
      refundPeriodInDays: offer.refundPeriodInDays ?? null,
      offerLogo: display.logo || '',
      offerDescription: display.description || '',
      offerTerms: display.termsAndConditions || '',
      offerBanner: display.banner || '',
      ctaBanner: display.ctaBanner || { image: '', link: '', buttonText: '' },
      sectionTitle: display.sectionTitle || '',
      sectionDescription: display.sectionDescription || '',
      ctaTiles: display.ctaTiles || [],
      videoSectionTitle: display.videoSectionTitle || '',
      videoSectionDescription: display.videoSectionDescription || '',
      videoCta: display.videoCta || { videoUrl: '', posterImage: '', link: '', buttonText: '' },
      link: display.link || '',
      categories: display.categories || [],
      lengthOfTime: offer.activationLengthInDays ?? undefined,
      currency: offer.currencyList || [],
      country: offer.country || '',
      offerType: offer.offerType || '',
      matchAgainst: offer.validFor || 'ALL_CHANNELS',
      superMatch: !!offer.superMatch,
      unenrichedCreditorsStrings: offer.unenrichedCreditorsStrings || [],
      channels: offer.channels || [],
      // Normalise legacy customer types (NEW/ALL/EXISTING/LAPSED) to the two the
      // matcher supports, so an older offer loads into a valid dropdown option.
      cashbackRules:
        offer.cashbackRules && offer.cashbackRules.length
          ? offer.cashbackRules.map((r) => ({
              ...r,
              customerType:
                (r.customerType || '').toUpperCase() === 'NEW' || r.customerType === 'NEW_CUSTOMER'
                  ? 'NEW_CUSTOMER'
                  : 'EXISTING_CUSTOMER',
            }))
          : this.offerInformation.cashbackRules,
    })
  },

  // Step completion flags used by the stepper to enable/disable steps.
  get retailerComplete() {
    return (
      this.selectedRetailer ||
      (this.retailer.retailerName !== '' && this.retailer.retailerLogoFile !== undefined)
    )
  },
  get transactionComplete() {
    return this.selectedRetailer || this.enrichment !== undefined
  },
  get midsComplete() {
    return this.channelsConfirmed
  },
  get offerInfoComplete() {
    // Supplier is set server-side (the publisher), no longer entered in the wizard, so it is not
    // part of completeness - requiring it here blocked progressing past the offer-info step.
    return (
      this.offerInformation.offerName !== '' &&
      !!this.offerInformation.offerDescription &&
      this.offerInformation.offerType !== ''
    )
  },
})
